import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { 
  Zap, Search, Shield, Coins, Users, Link, Globe, Lock, Layers, 
  Brain, CheckCircle, Scale, Workflow, Database, ArrowLeft, 
  ExternalLink, Send, ChevronDown, Play
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Toaster } from "sonner";
import { landingData, landingLabels } from "../data/landingData";
import { trackFormSubmit, trackCTAClick, trackLandingView } from "../utils/analytics";
import { useRecaptchaToken } from "../hooks/useRecaptchaToken";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// External demo URLs per solution (partner landings, affiliate URLs)
const EXTERNAL_DEMO_URLS = {
  yooz: "https://www.getyooz.com/es/?utm_campaign=utm_partner_madebypartnertool&utm_source=bing&utm_medium=cpc&utm_term=yooz&utm_content=yooz"
};

// Icon mapper
const iconMap = {
  zap: Zap, search: Search, shield: Shield, coins: Coins, users: Users,
  link: Link, globe: Globe, lock: Lock, layers: Layers, brain: Brain,
  "check-circle": CheckCircle, scale: Scale, workflow: Workflow, database: Database
};

// Detect language from localStorage or browser
const detectLang = () => {
  const saved = localStorage.getItem("documatch_language");
  if (saved && ["fr", "es", "en"].includes(saved)) return saved;
  const bl = (navigator.language || "").toLowerCase();
  if (bl.startsWith("es")) return "es";
  if (bl.startsWith("fr")) return "fr";
  return "en";
};

export default function SolutionLanding() {
  const { solutionId } = useParams();
  const navigate = useNavigate();
  const [language, setLanguage] = useState(detectLang);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", erp: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const getRecaptchaToken = useRecaptchaToken();

  const solution = landingData[solutionId];
  const labels = landingLabels[language];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (solutionId) trackLandingView(solutionId);
  }, [solutionId]);

  if (!solution) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">Solution not found</h1>
          <Button onClick={() => navigate("/")} data-testid="landing-back-home">
            <ArrowLeft size={16} className="mr-2" /> {labels.backHome}
          </Button>
        </div>
      </div>
    );
  }

  const hero = solution.hero[language];
  const benefits = solution.benefits[language];
  const useCases = solution.useCases[language];
  const roi = solution.roi[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;
    setSending(true);
    try {
      const recaptchaToken = await getRecaptchaToken("landing_demo");
      const messageWithErp = formData.erp ? `[ERP: ${formData.erp}]\n${formData.message || ""}` : formData.message;
      await axios.post(`${API}/request-demo`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: messageWithErp,
        solution: solution.id,
        language,
        recaptchaToken
      });
      setSent(true);
      toast.success(labels.formSuccess);
      trackFormSubmit("landing_demo", solution.id);
    } catch (err) {
      console.error("Landing demo error:", err?.response?.data || err);
      toast.error("Error sending request");
    } finally {
      setSending(false);
    }
  };

  const langOptions = [
    { code: "fr", label: "FR" },
    { code: "es", label: "ES" },
    { code: "en", label: "EN" }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid={`landing-${solutionId}`}>
      <Toaster position="top-right" richColors />
      
      {/* Sticky top bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-14 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")} 
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors"
            data-testid="landing-nav-back"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">DOCUMATCH</span>
          </button>
          <div className="flex items-center gap-3">
            <img src={solution.logo} alt={solution.id} className="h-7 object-contain" />
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {langOptions.map(l => (
                <button
                  key={l.code}
                  onClick={() => { setLanguage(l.code); localStorage.setItem("documatch_language", l.code); }}
                  className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                    language === l.code ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                  data-testid={`landing-lang-${l.code}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section 
        className="pt-24 pb-16 md:pt-32 md:pb-24 px-4"
        style={{ background: `linear-gradient(135deg, ${solution.color}08 0%, ${solution.color}15 50%, white 100%)` }}
        data-testid="landing-hero"
      >
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img 
              src={solution.logo} 
              alt={solution.id} 
              className="h-16 md:h-20 object-contain mx-auto mb-8"
            />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              {hero.title}
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
              {hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="text-base px-8 py-3 shadow-lg"
                style={{ backgroundColor: solution.color }}
                onClick={() => {
                  const externalUrl = EXTERNAL_DEMO_URLS[solution.id];
                  if (externalUrl) {
                    trackCTAClick(`${solution.id}_demo_external`);
                    window.open(externalUrl, "_blank", "noopener,noreferrer");
                  } else {
                    document.getElementById("landing-form").scrollIntoView({ behavior: "smooth" });
                  }
                }}
                data-testid="landing-hero-cta"
              >
                {hero.cta}
              </Button>
              {/* External website links removed — all traffic stays inside Documatch */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 px-4 bg-white" data-testid="landing-benefits">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-12"
          >
            {benefits.title}
          </motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.items.map((item, i) => {
              const Icon = iconMap[item.icon] || Zap;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-slate-50 rounded-xl p-6 hover:shadow-md transition-shadow"
                  data-testid={`landing-benefit-${i}`}
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${solution.color}15` }}
                  >
                    <Icon size={20} style={{ color: solution.color }} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 md:py-20 px-4 bg-slate-50" data-testid="landing-usecases">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-12"
          >
            {useCases.title}
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {useCases.items.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-sm border border-slate-100"
                data-testid={`landing-usecase-${i}`}
              >
                <div 
                  className="w-8 h-1 rounded-full mb-4"
                  style={{ backgroundColor: solution.color }}
                />
                <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI */}
      <section className="py-16 md:py-20 px-4" style={{ backgroundColor: solution.color }} data-testid="landing-roi">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-white text-center mb-12"
          >
            {roi.title}
          </motion.h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {roi.items.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/15 backdrop-blur-sm rounded-xl p-6 text-center"
                data-testid={`landing-roi-${i}`}
              >
                <p className="text-3xl md:text-4xl font-bold text-white mb-2">{item.value}</p>
                <p className="text-sm text-white/80">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube Video OR Hero Image */}
      <section className="py-16 md:py-20 px-4 bg-slate-50" data-testid="landing-video">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10"
          >
            {(solution.videoTitleOverride && solution.videoTitleOverride[language]) || labels.videoTitle}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="aspect-video rounded-2xl overflow-hidden shadow-xl"
          >
            {solution.heroImage ? (
              <img
                src={solution.heroImage}
                alt={`${solution.id} showcase`}
                className="w-full h-full object-cover"
                data-testid="landing-hero-image"
              />
            ) : (solution.youtube && solution.id === "yooz") ? (
              <iframe
                src={solution.youtube}
                title={`${solution.id} video`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                data-testid="landing-youtube-iframe"
              />
            ) : null}
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="landing-form" className="py-16 md:py-20 px-4 bg-white" data-testid="landing-form-section">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              {labels.formTitle}
            </h2>
            <p className="text-slate-600">{labels.formSubtitle}</p>
          </motion.div>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-50 border border-green-200 rounded-2xl p-10 text-center"
              data-testid="landing-form-success"
            >
              <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-green-800">{labels.formSuccess}</p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onSubmit={handleSubmit}
              className="space-y-4 bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-100"
              data-testid="landing-demo-form"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{labels.formName} *</label>
                  <Input
                    required
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="John Doe"
                    data-testid="landing-form-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{labels.formEmail} *</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    placeholder="john@company.com"
                    data-testid="landing-form-email"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{labels.formPhone}</label>
                  <Input
                    value={formData.phone}
                    onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                    placeholder="+33 6 12 34 56 78"
                    data-testid="landing-form-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{labels.formCompany}</label>
                  <Input
                    value={formData.company}
                    onChange={e => setFormData(p => ({ ...p, company: e.target.value }))}
                    placeholder="Company Inc."
                    data-testid="landing-form-company"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {language === "fr" ? "ERP utilisé" : language === "es" ? "ERP utilizado" : "ERP used"}
                  <span className="text-slate-400 text-xs font-normal ml-1">({language === "fr" ? "optionnel" : language === "es" ? "opcional" : "optional"})</span>
                </label>
                <Input
                  value={formData.erp}
                  onChange={e => setFormData(p => ({ ...p, erp: e.target.value }))}
                  placeholder="Sage, SAP, Odoo..."
                  data-testid="landing-form-erp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{labels.formMessage}</label>
                <textarea
                  value={formData.message}
                  onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                  rows={3}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="landing-form-message"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                className="w-full py-3 text-base"
                style={{ backgroundColor: solution.color }}
                data-testid="landing-form-submit"
              >
                {sending ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    ...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send size={16} />
                    {labels.formSubmit}
                  </span>
                )}
              </Button>
            </motion.form>
          )}
        </div>
      </section>

      {/* Bottom CTA - Compare all solutions */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-slate-800 to-slate-900" data-testid="landing-bottom-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {labels.ctaBottom}
            </h2>
            <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
              {labels.ctaBottomSub}
            </p>
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base shadow-lg"
              onClick={() => navigate("/#analyzer")}
              data-testid="landing-compare-cta"
            >
              {labels.ctaBottomBtn}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="py-6 px-4 bg-slate-950 text-center">
        <p className="text-sm text-slate-500">
          Documatch &copy; {new Date().getFullYear()} &mdash;{" "}
          <button onClick={() => navigate("/")} className="underline hover:text-slate-300 transition-colors">
            documatch.eu
          </button>
        </p>
      </footer>
    </div>
  );
}
